<script setup>
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CincelAPI from 'dashboard/api/integrations/cincel';
import TicketFormsAPI from 'dashboard/api/ticketForms';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

defineProps({
  teamUuid: { type: String, default: '' },
});

const { t } = useI18n();

const templates = ref([]);
const forms = ref([]);
const formTemplates = ref([]);
const isLoading = ref(false);
const editing = ref(null); // the template object currently being edited (or null)

const emptyTemplate = () => ({
  id: null,
  name: '',
  cincel_form_template_uuid: '',
  signature_kind: 'advanced',
  fieldMapRows: [{ field: '', source: '' }],
  signer_map: { name: '', email: '', role: 'firmante', order: 0 },
  formIds: [],
});

const toEditModel = template => {
  const fieldMap = template.field_map || {};
  const fieldMapRows = Object.entries(fieldMap).map(([field, source]) => ({
    field,
    source,
  }));
  return {
    id: template.id,
    name: template.name || '',
    cincel_form_template_uuid: template.cincel_form_template_uuid || '',
    signature_kind: template.signature_kind || 'advanced',
    fieldMapRows: fieldMapRows.length ? fieldMapRows : [{ field: '', source: '' }],
    signer_map: {
      name: template.signer_map?.name || '',
      email: template.signer_map?.email || '',
      role: template.signer_map?.role || 'firmante',
      order: template.signer_map?.order || 0,
    },
    formIds: forms.value
      .filter(form => form.cincel_signature_template_id === template.id)
      .map(form => form.id),
  };
};

const loadAll = async () => {
  isLoading.value = true;
  try {
    const [templatesRes, formsRes] = await Promise.all([
      CincelAPI.listTemplates(),
      TicketFormsAPI.get(),
    ]);
    templates.value = templatesRes.data || [];
    forms.value = formsRes.data || [];
    try {
      const { data } = await CincelAPI.getFormTemplates();
      formTemplates.value = Array.isArray(data) ? data : [];
    } catch (error) {
      formTemplates.value = [];
    }
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const startCreate = () => {
  editing.value = emptyTemplate();
};

const startEdit = template => {
  editing.value = toEditModel(template);
};

const cancelEdit = () => {
  editing.value = null;
};

const addFieldRow = () => {
  editing.value.fieldMapRows.push({ field: '', source: '' });
};

const removeFieldRow = index => {
  editing.value.fieldMapRows.splice(index, 1);
};

const toggleForm = formId => {
  const list = editing.value.formIds;
  const idx = list.indexOf(formId);
  if (idx === -1) list.push(formId);
  else list.splice(idx, 1);
};

const buildPayload = () => {
  const fieldMap = {};
  editing.value.fieldMapRows.forEach(row => {
    if (row.field.trim()) fieldMap[row.field.trim()] = row.source.trim();
  });
  return {
    name: editing.value.name.trim(),
    cincel_form_template_uuid: editing.value.cincel_form_template_uuid.trim(),
    signature_kind: editing.value.signature_kind,
    field_map: fieldMap,
    signer_map: {
      name: editing.value.signer_map.name.trim(),
      email: editing.value.signer_map.email.trim(),
      role: editing.value.signer_map.role.trim() || 'firmante',
      order: Number(editing.value.signer_map.order) || 0,
    },
  };
};

const save = async () => {
  try {
    const payload = buildPayload();
    let templateId = editing.value.id;
    if (templateId) {
      await CincelAPI.updateTemplate(templateId, payload);
    } else {
      const { data } = await CincelAPI.createTemplate(payload);
      templateId = data.id;
    }
    await CincelAPI.connectForms(templateId, editing.value.formIds);
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.SAVE_SUCCESS'));
    editing.value = null;
    await loadAll();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.ERROR'));
  }
};

const remove = async template => {
  try {
    await CincelAPI.deleteTemplate(template.id);
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.DELETE_SUCCESS'));
    await loadAll();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.ERROR'));
  }
};

onMounted(loadAll);
</script>

<template>
  <div
    class="flex flex-col gap-4 p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
  >
    <div class="flex items-start justify-between">
      <div>
        <h4 class="text-lg font-medium text-n-slate-12">
          {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.HEADER') }}
        </h4>
        <p class="text-n-slate-11 text-sm leading-6">
          {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.DESCRIPTION') }}
        </p>
      </div>
      <Button
        v-if="!editing"
        faded
        blue
        :label="t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.ADD_BUTTON')"
        @click="startCreate"
      />
    </div>

    <!-- List -->
    <div v-if="!editing" class="flex flex-col gap-2">
      <p
        v-if="!isLoading && !templates.length"
        class="text-n-slate-11 text-sm"
      >
        {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.EMPTY') }}
      </p>
      <div
        v-for="template in templates"
        :key="template.id"
        class="flex items-center justify-between p-3 rounded-md border border-n-weak"
      >
        <div>
          <p class="text-sm font-medium text-n-slate-12">{{ template.name }}</p>
          <p class="text-xs text-n-slate-11">
            {{ template.signature_kind }} · {{ template.cincel_form_template_uuid }}
          </p>
        </div>
        <div class="flex gap-2">
          <Button
            sm
            faded
            slate
            :label="t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.EDIT')"
            @click="startEdit(template)"
          />
          <Button
            sm
            faded
            ruby
            :label="t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.DELETE')"
            @click="remove(template)"
          />
        </div>
      </div>
    </div>

    <!-- Editor -->
    <div v-else class="flex flex-col gap-4">
      <Input
        v-model="editing.name"
        :label="t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.NAME.LABEL')"
        :placeholder="
          t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.NAME.PLACEHOLDER')
        "
      />

      <label class="text-sm font-medium text-n-slate-12">
        {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.TEMPLATE.LABEL') }}
        <select
          v-if="formTemplates.length"
          v-model="editing.cincel_form_template_uuid"
          class="mt-1 block w-full rounded-md border border-n-weak bg-n-alpha-3 text-n-slate-12 text-sm py-2 px-3"
        >
          <option value="">—</option>
          <option v-for="ft in formTemplates" :key="ft.uuid" :value="ft.uuid">
            {{ ft.name || ft.uuid }}
          </option>
        </select>
        <Input v-else v-model="editing.cincel_form_template_uuid" class="mt-1" />
      </label>

      <label class="text-sm font-medium text-n-slate-12">
        {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.KIND.LABEL') }}
        <select
          v-model="editing.signature_kind"
          class="mt-1 block w-full rounded-md border border-n-weak bg-n-alpha-3 text-n-slate-12 text-sm py-2 px-3"
        >
          <option value="advanced">
            {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.KIND.ADVANCED') }}
          </option>
          <option value="autograph">
            {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.KIND.AUTOGRAPH') }}
          </option>
        </select>
      </label>

      <!-- Field map -->
      <div class="flex flex-col gap-2">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.FIELD_MAP.LABEL') }}
        </p>
        <div
          v-for="(row, index) in editing.fieldMapRows"
          :key="index"
          class="flex gap-2 items-center"
        >
          <Input
            v-model="row.field"
            class="flex-1"
            :placeholder="
              t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.FIELD_MAP.FIELD')
            "
          />
          <Input
            v-model="row.source"
            class="flex-1"
            :placeholder="
              t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.FIELD_MAP.SOURCE')
            "
          />
          <Button
            sm
            faded
            ruby
            icon="i-lucide-trash-2"
            @click="removeFieldRow(index)"
          />
        </div>
        <Button
          sm
          faded
          slate
          :label="
            t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.FIELD_MAP.ADD')
          "
          @click="addFieldRow"
        />
      </div>

      <!-- Signer map -->
      <div class="flex flex-col gap-2">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.SIGNER.LABEL') }}
        </p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Input
            v-model="editing.signer_map.name"
            :placeholder="
              t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.SIGNER.NAME')
            "
          />
          <Input
            v-model="editing.signer_map.email"
            :placeholder="
              t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.SIGNER.EMAIL')
            "
          />
          <Input
            v-model="editing.signer_map.role"
            :placeholder="
              t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.SIGNER.ROLE')
            "
          />
        </div>
      </div>

      <!-- Connect to forms -->
      <div class="flex flex-col gap-2">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.FORMS.LABEL') }}
        </p>
        <p v-if="!forms.length" class="text-n-slate-11 text-sm">
          {{ t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.FORMS.EMPTY') }}
        </p>
        <label
          v-for="form in forms"
          :key="form.id"
          class="flex items-center gap-2 cursor-pointer text-sm text-n-slate-12"
        >
          <input
            type="checkbox"
            :checked="editing.formIds.includes(form.id)"
            @change="toggleForm(form.id)"
          />
          {{ form.name }}
        </label>
      </div>

      <div class="flex justify-end gap-2">
        <Button
          faded
          slate
          :label="t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.CANCEL')"
          @click="cancelEdit"
        />
        <Button
          blue
          :label="t('INTEGRATION_SETTINGS.CINCEL.SIGNATURE_TEMPLATES.SAVE')"
          @click="save"
        />
      </div>
    </div>
  </div>
</template>
